import {useState, useEffect, useMemo} from 'react';
import Head from 'next/head';
import track from '@/utils/tracking/google';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';
import {MENU_STATIC_PAGES, STATIC_PAGES, STATIC_PAGES_DESCRIPTION_MAP} from "@/const";

export default function HeadTag({ desc }) {
    const router = useRouter();
    const { asPath = "" } = router;
    // TODO：更新新站点后，gameyeap.com需要修改
    const canonicalUrl = `https://www.gameyeap.com${router.asPath === '/' ? '' : router.asPath}`;
    const { currentGame } = useSelector((state) => state.game);

    // 是游戏详情页
    const isGamePath = useMemo(() => {
        return asPath.includes("/game") && !MENU_STATIC_PAGES.some((path) => path === asPath) && currentGame?.name;
    }, [asPath]);

    // 生成页面标题的函数
    const generateTitle = () => {
        // 如果是游戏页面
        if (isGamePath) {
            return `${currentGame?.name} | Play Free Online Games for Fun`;
        }

        // 如果是静态页面且不是首页
        if (STATIC_PAGES.includes(asPath) && asPath !== '/') {
            let pageName = asPath.replace('/game/', '');
            
            // 特殊页面只首字母大写
            if (['/about', '/contact', '/privacy-policy'].includes(pageName)) {
                pageName = pageName.replace('/', '');
                if (pageName === 'privacy-policy') {
                    return 'Privacy Policy';
                }
                return pageName.charAt(0).toUpperCase() + pageName.slice(1);
            }

            if (pageName === 'all-game') {
                return "ALL Game | Play Free Online Games for Fun";
            }
            
            // 游戏分类页面添加"Game"后缀
            return `${pageName.charAt(0).toUpperCase() + pageName.slice(1)} Game | Play Free Online Games for Fun`;
        }

        // 默认首页标题
        return 'Gameyeap | Play Free Online Games for Fun';
    };

    // 判断是否需要显示description
    const shouldShowDescription = () => {
        // 如果是游戏页面，显示description
        if (isGamePath) {
            return true;
        }

        if (["/about", "/contact", ...MENU_STATIC_PAGES].some((page) => page === asPath)) {
            return true;
        }
        
        // 如果是首页，显示description
        if (asPath === '/') {
            return true;
        }
        
        // 其他静态页面不显示description
        return false;
    };

    // 生成description内容
    const generateDescription = () => {
        if (isGamePath && currentGame?.description) {
            const text = currentGame.description.trim();
            
            // 如果文本长度小于等于150个字符，直接返回
            if (text.length <= 150) {
                return text;
            }
            
            // 在前150个字符中，从后往前查找第一个"."或"!"
            const searchText = text.substring(0, 150);
            let lastPunctuation = -1;
            
            // 从后往前查找"."或"!"
            for (let i = searchText.length - 1; i >= 0; i--) {
                if (searchText[i] === '.' || searchText[i] === '!') {
                    lastPunctuation = i;
                    break;
                }
            }
            
            if (lastPunctuation !== -1) {
                // 找到了"."或"!"，截取到该位置（包含标点符号）
                return text.substring(0, lastPunctuation + 1).trim();
            } else {
                // 没有找到"."或"!"，返回前150个字符
                return text.substring(0, 150).trim();
            }
        }

        // 针对静态页面
        if (["/about", "/contact", ...MENU_STATIC_PAGES].some((page) => page === asPath)) {
            return STATIC_PAGES_DESCRIPTION_MAP[asPath];
        }

        return 'Play online games for free at Gameyeap!! The best place to play amazing browser games without installation. Have Fun!';
    };

    const pageTitle = generateTitle();

    useEffect(() => {
        track.initGoogleAnalytics({ googleId: 'AW-17085889980' });
        track.initGoogleAnalytics({ googleId: 'G-0R6W5KHL7P' });
    }, []);

    return (
        <Head>
            <title>{pageTitle}</title>
            <meta
                content="Welcome to Gameyeap.com, a fantastic new paradise for online games lovers.Gameyeap.com always pays a lot of time to deliver new games and special games designed for you.The goal of the portal is to offer the most addictive games to our gamers.Gameyeap.com collects the best free mobile games daily and you can play these html5 games online directly without installation on your smartphone, android mobile, iPhone, iPad or tablet for free. We will keep updating new online games daily and design the best experience for our gamers while enjoying the gaming world in Gameyeap.com"
                itemProp="name"
            ></meta>
            {shouldShowDescription() && (
                <meta name="description" content={generateDescription()}></meta>
            )}
            <meta name="keywords"
                  content="free online games, mobile, block blast, sprunki incredibox, gameyeap.com, games for teens"/>
            <meta name="title" content={pageTitle}/>
            <meta content="website" property="og:type"></meta>
            <meta name="viewport"
                  content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no"></meta>
            <meta name="baidu-site-verification" content="codeva-ItrsNIwyNB"/>
            <meta httpEquiv="content-type" content="text/html;charset=utf-8"/>
            <link rel="canonical" href={canonicalUrl}/>
            <script async
                    src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-7450741884719342"
                    crossOrigin="anonymous"></script>
        </Head>
    );
}
